import CollectionList from "./CollectionList";

export { CollectionSearch as default };

/**
* Collection Filter
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class CollectionSearch extends CollectionList {
  /**
   * Search Rules method mapping
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  static RULES = {
    'lower_case' : 'applyRuleLowerCase',
    'latinize'   : 'applyRuleLatinize',
  };

  /**
   * List of search matched terms
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  matches = {};

  /**
   * Defines if search is active
   *
   * @public
   *
   * @type {Boolean}
   *
   * @since 7.0.0
   */
  search_active = false;

  /**
   * Defines the list to be used in searches
   *
   * @public
   *
   * @type {CollectionList}
   *
   * @since 7.0.0
   */
  list;

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {string} index_id
   *
   * @return {void}
   */
  constructor(index_id) {
    super(index_id);
  }

  /**
   * Sets the search list to be used
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {CollectionList} list
   *
   * @return {void}
   */
  setSearchList(list) {
    this.list = list;
  }

  /**
   * Searches item fields applying the defined search rules
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} search_term   search term
   * @param {Object} search_fields fields to be searched
   * @param {Number} limit         optional, results limit
   *
   * @return {void}
   */
  search(search_term, search_fields, limit = 0) {
    let modified       = this.createModificationItem();
    search_term        = search_term.trim().toLowerCase() || '';
    this.setSearchActive(true);

    if (search_term.length === 0) {
      return;
    }

    this.list.items.forEach(item => {
      if (modified.count > limit && limit !== 0) {
        return;
      }

      if (this.applyRules(item, search_term, search_fields) === false) {
        return;
      }

      this.addObjectToModifiedItem(modified, item);
    });

    this.applyModifiedItem(modified);
  }

  /**
   * Applies all tules
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} item          item to be searched
   * @param {String} search_term   search term
   * @param {Object} search_fields fields to be searched
   *
   * @return {Boolean}
   */
  applyRules(item, search_term, search_fields) {
    for (let field in search_fields) {
      if (!item[field]) {
        continue;
      }

      let value = item[field];

      search_fields[field].forEach(rule => {
        value = this[CollectionSearch.RULES[rule]](value);
      });

      if (this.applySearch(item, value, search_term) === true) {
        return true;
      }
    }

    return false;
  }

  /**
   * Applies Lower Case filter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} item  item to be searched
   * @param {String} field field to be searched
   *
   * @return {String}
   */
  applyRuleLowerCase(value) {
    return value.toLowerCase();
  }

  /**
   * Applies Latinize filter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} item  item to be searched
   * @param {String} field field to be searched
   *
   * @return {void}
   */
  applyRuleLatinize(value) {
    return value.latinize();
  }

  /**
   * Applies the search, matches the searm term to field value
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} item        item to be searched
   * @param {String} field       field to be searched
   * @param {String} search_term search term
   *
   * @return {Boolean}
   */
  applySearch(item, value, search_term) {
    let matches = [];

    let index = value.indexOf(search_term.latinize());

    if (index === -1) {
      return false;
    }

    matches[0] = value.substring(0, index);
    matches[1] = value.substring(index, index + search_term.length);
    matches[2] = value.substring(index + search_term.length);

    this.matches[item[this.index_id]] = matches;
    return true;
  }

  /**
   * Creates a modification item to apply filters
   *
   * @protected
   *
   * @since 7.0.0
   *
   * @return {Object
   */
  createModificationItem() {
    return {
      items : [],
      index : {},
      count : 0,
    }
  }

  /**
   * Adds a contact to the modified item
   *
   * @protected
   *
   * @since 7.0.0
   *
   * @param {Object} modified item beeing modified
   * @param {Object} object   object to add
   *
   * @return {void}
   */
  addObjectToModifiedItem(modified, object) {
    modified.items.push(object);
    modified.index[object[this.index_id]] = object;
    modified.count++;
  }

  /**
   * Applies item modifications to the current collection
   *
   * @protected
   *
   * @since 7.0.0
   *
   * @param {Object} modified item beeing modified
   *
   * @return {void}
   */
  applyModifiedItem(modified) {
    this.items = modified.items;
    this.index = modified.index;
    this.count = modified.count;
  }

  /**
   * Resets Search matches
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  resetMatches() {
    this.matches = {};
  }

  /**
   * Sets search avtive value
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setSearchActive(value) {
    this.search_active = value;
  }

  /**
   * Resets collection to it's initial state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reset() {
    this.setSearchActive(false);
    this.resetMatches();
    super.reset();
  }
}